<script lang="ts" setup>
const { t: $t } = useI18n()

import type { SelectOption } from 'naive-ui'
import type { CustomerModuleDefectCreateOrUpdatePayload } from '~/layers/customer-module/types'
import { DefectType } from '~/prisma/enums'

const props = defineProps<{
  payload?: CustomerModuleDefectCreateOrUpdatePayload
  accessCodeSession?: string
}>()
const emit = defineEmits<{ (e: 'close'): void }>()

const id = computed(() => props.payload?.mode === 'update' ? props.payload.id : undefined)
const offerId = computed(() => props.payload?.entity === 'offer' ? props.payload.entityId : undefined)
const accessCodeSession = computed(() => props.accessCodeSession)
const where = computed(() => ({
  type: { in: ['machinery', 'machineryAccessory', 'machineryAccessoryCategory', 'itemSetAccessory'] },
}))

const { defectPublicQuery, offerPublicQuery, accountQuery } = useCustomerModuleQuery()
const { data: defect, isLoading } = defectPublicQuery.getById(id, accessCodeSession)
const { data: offerPositions } = offerPublicQuery.getOfferPositionsById(offerId, where, accessCodeSession, computed(() => !!offerId.value))
const { data: account } = accountQuery.getSelf()

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const upsert = useMutation({
  mutationFn: $trpc.customerModule.defect.publicUpsert.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t(`customerModule.defect.notification.${props.payload?.mode ?? 'create'}.error`) }),
  onSuccess: async ({ id, offerId, machineryId }) => {
    const invalidateQueries = [
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'defects', 'getById', id] }),
    ]
    if (offerId) {
      invalidateQueries.push(queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'defects', 'getAllByEntityId', 'offer', offerId] }))
    }
    if (machineryId) {
      invalidateQueries.push(queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'defects', 'getAllByEntityId', 'machinery', machineryId] }))
    }
    await Promise.all(invalidateQueries)

    notification.success({ title: $t(`customerModule.defect.notification.${props.payload?.mode}.success`), duration: 4500 })
    emit('close')
  },
})

const { useremail } = useAuthorization()

const defectTypeOptions = useTranslatedSelectOptions('customerModule.defect.type', Object.values(DefectType))

const positionOptions = computed(() => {
  if (props.payload?.entity === 'machinery') {
    return [{
      label: props.payload.entityId,
      value: props.payload.entityId,
    }]
  }

  if (!offerPositions.value) {
    return []
  }

  return offerPositions.value.reduce((acc, p) => {
    if (p.machinery) {
      acc.push({
        label: `${p.machinery.id} ${p.machinery.producerCompanyName} ${p.machinery.type.name}`,
        value: p.machinery.id,
      })
    }
    if (p.machineryAccessory) {
      acc.push({
        label: `${p.machineryAccessory.id} ${$t(`machineryAccessory.category.${p.machineryAccessory.category}`)}`,
        value: p.machineryAccessory.id,
      })
    }
    return acc
  }, [] as SelectOption[])
})

const defaultAlternativeContactPerson = computed(() => {
  if (!account.value?.contactPerson) {
    return undefined
  }

  return {
    name: account.value.contactPerson.name,
    phone: account.value.contactPerson.phoneNumberOffice ?? account.value.contactPerson.customer?.telephone ?? 'N/A',
  }
})

const payload = computed(() => {
  if (!props.payload) {
    return undefined
  }

  const { entity, entityId } = props.payload

  if (props.payload.mode === 'create') {
    return {
      [`${entity}Id`]: entityId,
      description: '',
      photos: [],
      type: DefectType.machinery,
      isForeignMachineryDefect: props.payload.isForeignMachineryDefect,
    }
  }

  if (defect.value) {
    const entityId = defect.value[`${entity}Id`]
    if (!entityId) {
      throw new Error('Entity ID is missing for defect')
    }

    return {
      ...defect.value,
      [`${entity}Id`]: entityId,
      type: defect.value.type,
    }
  }
})

const title = computed(() => {
  if (!props.payload) {
    return ''
  }

  if (props.payload.mode === 'update') {
    const entityId = defect.value?.machineryId ?? defect.value?.machineryAccessoryId
    return $t('customerModule.defect.update', { id: entityId })
  } else {
    return $t('customerModule.defect.create')
  }
})

const defectCreatedByOtherUser = computed(() => defect.value && (!defect.value.createdBy || defect.value.createdBy.email !== useremail.value))

const isFormDisabled = computed(() => {
  if (!props.payload) {
    return true
  }

  if (props.payload.mode === 'update' && (!defect.value || defect.value.repairedAt || defectCreatedByOtherUser.value)) {
    return true
  }

  return false
})
</script>

<template>
  <CustomerModulePopup
    :show="!!payload"
    :is-loading="isLoading"
    :is-spinning="upsert.isPending.value"
    :title
    @close="emit('close')"
  >
    <CustomerModuleDefectCreateOrUpdateForm
      v-if="props.payload && payload"
      :payload="payload"
      :mode="props.payload.mode"
      :position-options="positionOptions"
      :defect-type-options="defectTypeOptions"
      :default-alternative-contact-person="defaultAlternativeContactPerson"
      :is-form-disabled="isFormDisabled"
      @submit="upsert.mutate({
        ...$event,
        isAllowedForPublic: props.payload.mode === 'create' ? props.payload.isAllowedForPublic : undefined,
        accessCodeSession,
      })"
    />
  </CustomerModulePopup>
</template>
